import { useState, useEffect, useCallback } from 'react';
import { queryService } from '../services/queryService';

export interface SqlSchemaFormat {
    [tableName: string]: { label: string; type: 'keyword' }[] | string[];
}

interface UseSqlSchemaResult {
    schema: SqlSchemaFormat;
    isLoading: boolean;
    loadingStatus: string | null;
    error: string | null;
    refreshSchema: () => void;
}

const CACHE_PREFIX = 'sql_schema_v2_';

export const useSqlSchema = (terminalId: string | null, databaseType: string = 'ORACLE'): UseSqlSchemaResult => {
    const [schema, setSchema] = useState<SqlSchemaFormat>({});
    const [isLoading, setIsLoading] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const getCacheKey = useCallback(() => {
        return `${CACHE_PREFIX}${terminalId}_${databaseType}`;
    }, [terminalId, databaseType]);

    const fetchSchema = useCallback(async (forceRefresh = false) => {
        if (!terminalId) {
            setSchema({});
            setError(null);
            return;
        }

        const cacheKey = getCacheKey();

        if (!forceRefresh) {
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                try {
                    const parsed = JSON.parse(cached);
                    if (Object.keys(parsed).length > 0) {
                        setSchema(parsed);
                        setError(null); // clear any previous errors
                        return;
                    }
                } catch (e) {
                    console.warn("Invalid schema cache, fetching fresh.", e);
                }
            }
        }

        setIsLoading(true);
        setLoadingStatus('Fetching tables...');
        setError(null);

        try {
            // Fetch grouped schema (tables + columns) in one request
            const rawTablesData = await queryService.exploreSchema(terminalId, undefined, databaseType);

            const newSchema: SqlSchemaFormat = {};
            let tablesWithColumns = 0;

            if (Array.isArray(rawTablesData)) {
                rawTablesData.forEach((tableDef: any) => {
                    const name = typeof tableDef === 'string' ? tableDef : (tableDef.tableName || tableDef.name);
                    if (!name) return;
                    const cleanName = String(name).replace(/(^"|"$)/g, '');

                    // Extract columns from the grouped response
                    if (typeof tableDef === 'object' && Array.isArray(tableDef.columns) && tableDef.columns.length > 0) {
                        const tableCols: string[] = [];
                        tableDef.columns.forEach((c: any) => {
                            const cName = typeof c === 'string' ? c : (c.name || c.NAME || c.columnname || c.column_name || c.COLUMN_NAME);
                            if (cName) tableCols.push(String(cName).replace(/(^"|"$)/g, ''));
                        });
                        newSchema[cleanName] = tableCols;
                        if (tableCols.length > 0) tablesWithColumns++;
                    } else {
                        newSchema[cleanName] = [];
                    }
                });
            }

            const totalTables = Object.keys(newSchema).length;
            console.log(`[useSqlSchema] Grouped response: ${tablesWithColumns}/${totalTables} tables have columns.`);

            // If most tables already have columns from the grouped response, we are done
            if (tablesWithColumns > 0 && totalTables > 0) {
                setSchema(newSchema);
                try {
                    localStorage.setItem(cacheKey, JSON.stringify(newSchema));
                } catch (e) {
                    console.warn("Failed to cache schema", e);
                }
                setIsLoading(false);
                setLoadingStatus(null);
                return;
            }

            // Fallback: if the response had no columns at all (legacy flat response), 
            // check localStorage cache first
            const cachedRaw = localStorage.getItem(cacheKey);
            let existingSchema: SqlSchemaFormat = {};
            if (cachedRaw) {
                try {
                    existingSchema = JSON.parse(cachedRaw);
                } catch (e) {
                    console.warn("Invalid schema cache", e);
                }
            }

            const tableNames = Object.keys(newSchema);
            const missingTables: string[] = [];
            tableNames.forEach(t => {
                if (existingSchema[t] && existingSchema[t].length > 0) {
                    newSchema[t] = existingSchema[t];
                } else {
                    missingTables.push(t);
                }
            });

            if (missingTables.length === 0 && tableNames.length > 0) {
                setSchema(newSchema);
                localStorage.setItem(cacheKey, JSON.stringify(newSchema));
                setIsLoading(false);
                setLoadingStatus(null);
                return;
            }

            // Bulk fetch for missing tables
            if (missingTables.length > 20) {
                setLoadingStatus('Downloading full schema... this may take a moment');
                console.log(`[useSqlSchema] Fetching bulk schema for ${terminalId}...`);

                const bulkData = await queryService.exploreSchema(terminalId, 'ALL', databaseType);

                if (Array.isArray(bulkData)) {
                    bulkData.forEach((col: any) => {
                        const rawTName = col.tableName || col.TABLENAME || col.tablename || col.table_name || col.TABLE_NAME;
                        const rawCName = col.name || col.NAME || col.columnname || col.column_name || col.COLUMN_NAME;

                        if (rawTName && rawCName) {
                            const tName = String(rawTName).replace(/(^"|"$)/g, '');
                            const cName = String(rawCName).replace(/(^"|"$)/g, '');

                            if (newSchema[tName] !== undefined) {
                                (newSchema[tName] as string[]).push(cName);
                            } else {
                                const upperT = tName.toUpperCase();
                                const foundKey = Object.keys(newSchema).find(k => k.toUpperCase() === upperT);
                                if (foundKey) {
                                    (newSchema[foundKey] as string[]).push(cName);
                                }
                            }
                        }
                    });
                }
            } else {
                // Small number of missing tables -> batch fetch
                const BATCH_SIZE = 5;
                for (let i = 0; i < missingTables.length; i += BATCH_SIZE) {
                    const batch = missingTables.slice(i, i + BATCH_SIZE);
                    const currentIndex = Math.min(i + BATCH_SIZE, missingTables.length);
                    setLoadingStatus(`Loading columns: ${batch[0]} (${currentIndex}/${missingTables.length})`);
                    await Promise.all(batch.map(async (tableName) => {
                        try {
                            const colsData = await queryService.exploreSchema(terminalId, tableName, databaseType);
                            if (Array.isArray(colsData)) {
                                const columns: string[] = [];
                                colsData.forEach((col: any) => {
                                    if (col.tableName === tableName && Array.isArray(col.columns)) {
                                        columns.push(...col.columns.map((c: any) => typeof c === 'string' ? c : c.name).filter(Boolean));
                                    } else if (typeof col === 'string') {
                                        columns.push(col);
                                    } else if (typeof col === 'object') {
                                        const cName = col.name || col.NAME || col.columnname || col.column_name || col.COLUMN_NAME;
                                        if (cName) columns.push(String(cName).replace(/(^"|"$)/g, ''));
                                    }
                                });
                                if (columns.length > 0) newSchema[tableName] = columns;
                            }
                        } catch (colErr) {
                            console.warn(`Failed to fetch columns for ${tableName}`, colErr);
                        }
                    }));
                }
            }

            setSchema(newSchema);
            try {
                localStorage.setItem(cacheKey, JSON.stringify(newSchema));
            } catch (storageErr) {
                console.warn("Failed to cache schema to localStorage", storageErr);
            }
        } catch (err: any) {
            console.error("Failed to fetch SQL schema:", err);
            setError(err.message || 'Failed to load schema');
            // Check if we have anything to fallback to
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                try {
                    setSchema(JSON.parse(cached));
                } catch (e) {}
            } else {
                setSchema({});
            }
        } finally {
            setIsLoading(false);
            setLoadingStatus(null);
        }
    }, [terminalId, databaseType, getCacheKey]);

    useEffect(() => {
        let isMounted = true;

        // Wrap the fetch in a check to only update state if mounted
        const doFetch = async () => {
            // Let fetchSchema handle its internal state, we just call it
            await fetchSchema();
        };

        doFetch();

        return () => {
            isMounted = false;
        };
    }, [fetchSchema]);

    const refreshSchema = useCallback(() => {
        fetchSchema(true);
    }, [fetchSchema]);

    return { schema, isLoading, loadingStatus, error, refreshSchema };
};
